import posthog from 'posthog-js';
import { getTrackingConsent } from './consent';

const UTM_PROPERTIES = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
] as const;

export type AnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

const SAFE_PROPERTY_KEYS = new Set([
  'page',
  'cta',
  'cta_id',
  'destination',
  'format',
  'entry_count',
  'export_mode',
  'plan',
  'method',
  'provider',
  'platform',
  'environment',
  'app_version',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'value',
  'currency',
  'event_id',
  'workspace_id',
  'error_code',
  'status',
]);

let initialized = false;

const analyticsEnvironment =
  process.env.APP_ENV || (process.env.NODE_ENV === 'production' ? 'production' : 'development');

function captureAttribution() {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const attribution = Object.fromEntries(
    UTM_PROPERTIES.flatMap((key) => {
      const value = params.get(key)?.trim().slice(0, 200);
      return value ? [[key, value]] : [];
    }),
  );

  if (Object.keys(attribution).length > 0) {
    posthog.register({ environment: analyticsEnvironment, ...attribution });
  }
}

export function initAnalytics(): boolean {
  if (typeof window === 'undefined' || initialized || getTrackingConsent() !== 'granted') {
    return initialized;
  }

  const token = process.env.POSTHOG_PROJECT_TOKEN?.trim();
  if (!token) return false;

  posthog.init(token, {
    api_host: process.env.POSTHOG_HOST || 'https://us.i.posthog.com',
    defaults: '2026-05-30',
    capture_pageview: true,
    autocapture: false,
    cross_subdomain_cookie: true,
    persistence: 'localStorage+cookie',
    cookie_persisted_properties: [...UTM_PROPERTIES],
  });
  initialized = true;
  captureAttribution();
  return true;
}

export function captureEvent(event: string, properties: AnalyticsProperties = {}) {
  if (getTrackingConsent() !== 'granted' || !initAnalytics()) return;

  const safeProperties = Object.fromEntries(
    Object.entries({ ...properties, environment: analyticsEnvironment })
      .filter(([key, value]) => SAFE_PROPERTY_KEYS.has(key) && value !== undefined && value !== null)
      .map(([key, value]) => [
        key,
        typeof value === 'string' ? value.slice(0, 200) : value,
      ]),
  );
  posthog.capture(event, safeProperties);
}

export function identifyUser(userId: string) {
  if (!userId || getTrackingConsent() !== 'granted' || !initAnalytics()) return;
  posthog.identify(userId);
}

export function resetAnalytics() {
  if (!initialized) return;
  posthog.reset();
}

export function createEventId(prefix = 'event'): string {
  const randomId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}_${randomId}`;
}

export function normalizeErrorCode(message: string): string {
  const normalized = message.toLowerCase();
  if (normalized.includes('email already registered')) return 'email_already_registered';
  if (normalized.includes('invalid credentials')) return 'invalid_credentials';
  if (normalized.includes('network')) return 'network_error';
  return 'unknown';
}
